import React, { useMemo } from 'react';

import QuickFilters from 'components/Filters/Common/QuickFilters';
import { COMPONENTS, SECTIONS } from 'components/Globals/Analytics/constants';
import ProductionListView from 'components/Productions/Common/ProductionListView';

import { TP, DATE_FORMATS, PRODUCTION_VALIDITY_IDENTIFIERS, SORT_OPTION_VALUES } from 'constants/index';

import { createDate } from 'utils/date';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import useNavigate from 'utils/hooks/useNavigate';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES, FILTER_TYPES, VIEW_MODES } from 'operabase-router/constants';

import FeaturedEntitySection from '../Common/FeaturedEntitySection';

import classes from './FeaturedProductions.module.scss';

const trackingData = {
  section: SECTIONS.FEATURED_PRODUCTIONS,
};

const FeaturedProductions = () => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const navigate = useNavigate();
  const { isDesktop } = useDeviceTypeLayouts();
  const linkProps = useMemo(() => navigate.getLinkProps({ entityType: ENTITY_TYPES.PRODUCTION }), [navigate]);

  const filters = useMemo(() => {
    const today = createDate(new Date()).format(DATE_FORMATS.FULL_DATE);

    return {
      [FILTER_TYPES.DATE_FROM]: today,
      is_producer_approved: true,
      has_trailer: true,
      has_media: true,
      enable_ml: true,
      approved_by: PRODUCTION_VALIDITY_IDENTIFIERS.ADMIN_AND_ORG,
      car: true, // NOTE: caches the results for 5 mins
    };
  }, []);

  const quickFilterOptions = useMemo(
    () => [
      {
        name: t(`${TP}.m_ASKLOCATION`),
        filterType: FILTER_TYPES.COUNTRY,
      },
      {
        name: t(`${TP}.FN_GENRE_STAGING`),
        filterType: FILTER_TYPES.WORK_TYPE,
      },
      {
        name: t(`${TP}.m_COMPOSERS`),
        filterType: FILTER_TYPES.COMPOSER,
      },
      {
        name: t(`${TP}.FN_MUSICAL_WORKS`),
        filterType: FILTER_TYPES.WORK,
      },
      {
        name: t(`${TP}.m_VENUE`),
        filterType: FILTER_TYPES.CONTRIBUTION_EXPRESSION,
        subFilterType: { scrollTo: FILTER_TYPES.VENUE },
      },
    ],
    [t],
  );

  return (
    <FeaturedEntitySection
      title={t(`${TP}.FN_PERFORMANCE_AND_PRODUCTIONS`)}
      linkProps={linkProps}
      hideViewAll
      trackingData={trackingData}
    >
      <QuickFilters
        prefixOptions={quickFilterOptions}
        variant="h3"
        showQuickViewFilterOption
        trackingData={{
          ...trackingData,
          component: COMPONENTS.QUICK_FILTERS,
        }}
        quickViewFilterOptionProps={{
          endPlacement: true,
        }}
        scrollable
      />
      <ProductionListView
        overrideFilters={filters}
        infinite={false}
        limit={6}
        forcedViewMode={isDesktop ? VIEW_MODES.GRID : VIEW_MODES.MINI}
        forcedSortBy={`${SORT_OPTION_VALUES.FEATURED_DESC},${SORT_OPTION_VALUES.YEAR_ASC},${SORT_OPTION_VALUES.REVIEWS_COUNT_DESC}`}
        styles={{
          list: classes.productionListView,
        }}
        hideActions
        trackingData={trackingData}
        showSortOptions
      />
    </FeaturedEntitySection>
  );
};

export default FeaturedProductions;
